import React, { useState } from 'react';
import styled from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { shimmerText } from './activityShimmer';

interface Props {
  files: string[];
}

// Live indicator shown while freshly-uploaded files are being OCR'd/decoded on
// the backend. Collapsed by default: the header reads "Extraindo N arquivos"
// (shimmering, with a spinner); expanding lists which files are in the batch.
// Gives feedback now that the user can upload many files at once.
const ExtractingFilesBlock: React.FC<Props> = ({ files }) => {
  const [expanded, setExpanded] = useState(false);
  if (!files || files.length === 0) return null;
  const n = files.length;

  return (
    <Row data-testid="extracting-files-block">
      <Header onClick={() => setExpanded((v) => !v)}>
        <Spinner>
          <CircularProgress size={13} thickness={5} sx={{ color: 'text.secondary' }} />
        </Spinner>
        <Label>Extraindo {n} {n === 1 ? 'arquivo' : 'arquivos'}</Label>
        <ChevronWrap>
          {expanded ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
        </ChevronWrap>
      </Header>

      {expanded && (
        <Files>
          {files.map((name, i) => (
            <FileRow key={`${name}-${i}`}>
              <InsertDriveFileOutlinedIcon style={{ fontSize: 14, opacity: 0.7, flexShrink: 0 }} />
              <FileName title={name}>{name}</FileName>
              <CircularProgress size={11} thickness={6} sx={{ color: 'text.secondary', opacity: 0.6, ml: 'auto', flexShrink: 0 }} />
            </FileRow>
          ))}
        </Files>
      )}
    </Row>
  );
};

export default ExtractingFilesBlock;

// ============ Styled Components ============

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2px 0;
  font-family: 'Sora', 'Lexend', sans-serif;
`;

const Header = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 2px 6px 2px 0;
  cursor: pointer;
  align-self: flex-start;
  border-radius: 6px;
  user-select: none;
  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)')};
  }
`;

const Spinner = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
`;

const Label = styled.span`
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.2;
  ${shimmerText}
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
  flex-shrink: 0;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
`;

const Files = styled.div`
  margin: 4px 0 6px 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  border-left: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)')};
  padding-left: 12px;
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_secondary || theme.font_color};
  max-width: 420px;
`;

const FileName = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;
